import { useEffect, useRef } from "react";
import * as THREE from "three";
import { motion } from "framer-motion";
import { Rocket } from "lucide-react";

const DREAM_LINES = [
  "Millions of ideas.",
  "Thousands of entrepreneurs.",
  "Growing businesses.",
  "New products.",
  "New technologies.",
  "New industries.",
  "New jobs.",
  "Stronger communities.",
  "A stronger economy.",
];

const INDIA_POLY = [
  [170, 10], [190, 42], [214, 70], [240, 120], [268, 142], [300, 150], [332, 132],
  [360, 140], [348, 168], [312, 172], [296, 196], [272, 206], [262, 224], [232, 256],
  [208, 302], [186, 362], [156, 416], [138, 356], [118, 300], [104, 248], [84, 212],
  [52, 206], [36, 176], [48, 150], [96, 96], [128, 56], [150, 30],
];

const CITY_POINTS = [
  [186, 112], [98, 246], [114, 264], [86, 190], [168, 286], [150, 332], [184, 346], [268, 202],
];
const HQ = [168, 286]; // Hyderabad

const SX = (x) => (x - 195) / 52;
const SY = (y) => -(y - 213) / 52;

function insidePolygon(x, y) {
  let c = false;
  for (let i = 0, j = INDIA_POLY.length - 1; i < INDIA_POLY.length; j = i++) {
    const [xi, yi] = INDIA_POLY[i];
    const [xj, yj] = INDIA_POLY[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) c = !c;
  }
  return c;
}

function India3D() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    } catch {
      return;
    }
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(50, mount.clientWidth / mount.clientHeight, 0.1, 100);
    camera.position.set(0, 0.4, 8.2);

    const group = new THREE.Group();
    scene.add(group);

    const shape = new THREE.Shape();
    INDIA_POLY.forEach(([x, y], i) => {
      if (i === 0) shape.moveTo(SX(x), SY(y));
      else shape.lineTo(SX(x), SY(y));
    });
    shape.closePath();

    const geo = new THREE.ExtrudeGeometry(shape, { depth: 0.3, bevelEnabled: false });
    const fill = new THREE.Mesh(
      geo,
      new THREE.MeshBasicMaterial({ color: 0x0d1a2b, transparent: true, opacity: 0.85 })
    );
    group.add(fill);
    const edges = new THREE.LineSegments(
      new THREE.EdgesGeometry(geo, 12),
      new THREE.LineBasicMaterial({ color: 0xff8800, transparent: true, opacity: 0.75 })
    );
    group.add(edges);

    // Interior glowing lights
    const lightPts = [];
    let guard = 0;
    while (lightPts.length < 240 && guard < 6000) {
      guard++;
      const x = 36 + Math.random() * 324;
      const y = 10 + Math.random() * 406;
      if (insidePolygon(x, y)) lightPts.push([SX(x), SY(y)]);
    }
    const lightPos = new Float32Array(lightPts.length * 3);
    const lightCol = new Float32Array(lightPts.length * 3);
    const palette = [new THREE.Color("#FF8800"), new THREE.Color("#00B0FF"), new THREE.Color("#00E676"), new THREE.Color("#ffffff")];
    lightPts.forEach(([x, y], i) => {
      lightPos[i * 3] = x;
      lightPos[i * 3 + 1] = y;
      lightPos[i * 3 + 2] = 0.34;
      const c = palette[i % 4];
      lightCol[i * 3] = c.r;
      lightCol[i * 3 + 1] = c.g;
      lightCol[i * 3 + 2] = c.b;
    });
    const lightGeo = new THREE.BufferGeometry();
    lightGeo.setAttribute("position", new THREE.BufferAttribute(lightPos, 3));
    lightGeo.setAttribute("color", new THREE.BufferAttribute(lightCol, 3));
    const lightMat = new THREE.PointsMaterial({
      size: 0.055,
      vertexColors: true,
      transparent: true,
      opacity: 0.95,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    group.add(new THREE.Points(lightGeo, lightMat));

    // City beacons + connection lines to HQ
    const cityMeshes = [];
    const lineVerts = [];
    CITY_POINTS.forEach(([x, y]) => {
      const m = new THREE.Mesh(
        new THREE.SphereGeometry(0.07, 12, 12),
        new THREE.MeshBasicMaterial({ color: 0xff8800 })
      );
      m.position.set(SX(x), SY(y), 0.36);
      group.add(m);
      cityMeshes.push(m);
      lineVerts.push(SX(x), SY(y), 0.34, SX(HQ[0]), SY(HQ[1]), 0.34);
    });
    const linkGeo = new THREE.BufferGeometry();
    linkGeo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(lineVerts), 3));
    const linkMat = new THREE.LineBasicMaterial({
      color: 0x00b0ff,
      transparent: true,
      opacity: 0.4,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    group.add(new THREE.LineSegments(linkGeo, linkMat));

    // Rising particles
    const rpN = 160;
    const rpPos = new Float32Array(rpN * 3);
    const rpSpeed = new Float32Array(rpN);
    for (let i = 0; i < rpN; i++) {
      rpPos[i * 3] = (Math.random() - 0.5) * 8;
      rpPos[i * 3 + 1] = (Math.random() - 0.5) * 9;
      rpPos[i * 3 + 2] = (Math.random() - 0.5) * 3;
      rpSpeed[i] = 0.004 + Math.random() * 0.01;
    }
    const rpGeo = new THREE.BufferGeometry();
    rpGeo.setAttribute("position", new THREE.BufferAttribute(rpPos, 3));
    const rpMat = new THREE.PointsMaterial({
      size: 0.03,
      color: 0x94a3b8,
      transparent: true,
      opacity: 0.55,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const rising = new THREE.Points(rpGeo, rpMat);
    scene.add(rising);

    const onResize = () => {
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mount.clientWidth, mount.clientHeight);
    };
    window.addEventListener("resize", onResize);

    let visible = true;
    const io = new IntersectionObserver(([e]) => (visible = e.isIntersecting), { threshold: 0 });
    io.observe(mount);

    let raf;
    let t = 0;
    const animate = () => {
      raf = requestAnimationFrame(animate);
      if (!visible) return;
      if (!reduced) t += 0.008;
      group.rotation.y = Math.sin(t * 0.5) * 0.38;
      group.rotation.x = 0.12;
      cityMeshes.forEach((m, i) => {
        const s = 1 + Math.sin(t * 3 + i) * 0.35;
        m.scale.setScalar(s);
      });
      if (!reduced) {
        const arr = rpGeo.attributes.position.array;
        for (let i = 0; i < rpN; i++) {
          arr[i * 3 + 1] += rpSpeed[i];
          if (arr[i * 3 + 1] > 4.5) arr[i * 3 + 1] = -4.5;
        }
        rpGeo.attributes.position.needsUpdate = true;
      }
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      window.removeEventListener("resize", onResize);
      [geo, edges.geometry, lightGeo, linkGeo, rpGeo].forEach((g) => g.dispose());
      [fill.material, edges.material, lightMat, linkMat, rpMat, ...cityMeshes.map((m) => m.material)].forEach((m) => m.dispose());
      cityMeshes.forEach((m) => m.geometry.dispose());
      mount.removeChild(renderer.domElement);
      renderer.dispose();
    };
  }, []);

  return <div ref={mountRef} data-testid="vision-india-3d" className="absolute inset-0 w-full h-full" />;
}

export function FinalVision() {
  return (
    <section data-testid="final-vision-section" className="relative overflow-hidden">
      {/* Long-term dream */}
      <div className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="font-mono-tech text-xs tracking-[0.35em] text-[#00B0FF] uppercase mb-10">The Long-Term Dream</p>
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-3 max-w-4xl mx-auto" data-testid="dream-lines">
            {DREAM_LINES.map((line, i) => (
              <motion.span
                key={line}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.09 }}
                className="text-base sm:text-xl text-slate-300 font-display"
              >
                {line}
              </motion.span>
            ))}
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9 }}
            className="mt-16"
          >
            <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight" data-testid="final-vision-statement">
              <span className="text-gradient-hero">BUILD MORE BUSINESSES.</span>
              <br />
              <span className="text-gradient-saffron">CREATE MORE OPPORTUNITIES.</span>
              <br />
              <span className="text-gradient-india">TRANSFORM MORE LIVES.</span>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mt-20 glass rounded-2xl p-8 sm:p-12 max-w-3xl mx-auto"
            data-testid="vision-statement-block"
          >
            <p className="font-mono-tech text-[10px] tracking-[0.4em] text-[#FF8800] uppercase mb-5">Our Vision</p>
            <p className="text-base sm:text-lg text-slate-200 leading-relaxed font-display">
              To build an entrepreneurial ecosystem that helps people transform ideas into
              businesses, businesses into opportunities, and opportunities into lasting
              economic and social impact.
            </p>
            <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed">
              LaunchLab is not just about launching companies.
              <br />
              <span className="text-gradient-saffron font-display font-bold">
                It is about launching possibilities.
              </span>
            </p>
          </motion.div>
        </div>
      </div>

      {/* Final screen — 3D India */}
      <div className="cinema relative h-[80vh] min-h-[540px] bg-[#03050A]" data-testid="vision-final-screen">
        <India3D />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1 }}
          >
            <div className="flex items-center justify-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF8800] to-[#ff5e00] flex items-center justify-center shadow-[0_0_40px_rgba(255,136,0,0.45)]">
                <Rocket className="w-6 h-6 text-[#06080E]" strokeWidth={2.4} />
              </div>
              <span className="font-display font-extrabold tracking-[0.2em] text-3xl sm:text-4xl text-white">
                LAUNCH<span className="text-[#FF8800]">LAB</span>
              </span>
            </div>
            <p className="font-display font-bold tracking-[0.25em] text-lg sm:text-2xl text-gradient-india" data-testid="vision-final-tagline">
              BUILD. LAUNCH. TRANSFORM.
            </p>
            <p className="mt-4 text-sm sm:text-base text-slate-400 max-w-md mx-auto">
              Building India's next generation of entrepreneurs and businesses.
            </p>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.4, delay: 1.6 }}
            className="mt-14 font-mono-tech text-xs sm:text-sm tracking-[0.45em] text-[#FF8800] uppercase"
            data-testid="vision-final-line"
          >
            2047 — The Vision Starts Today.
          </motion.p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-[#06080E] pointer-events-none" />
      </div>
    </section>
  );
}
